// Per-client Visit Verification — this client's clocked visits and where
// they stand: exceptions still open, anything the aggregator rejected, and
// what has been verified. Correcting and verifying stays on the desk, which
// is one click away with the same week loaded.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { supabase } from '../../lib/supabase';
import { EXCEPTION_LABELS, SEVERITY_STYLE } from '../../lib/scheduling';

const card = { background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 16 };
const th = { textAlign: 'left', fontSize: 10, color: 'var(--text3)', fontWeight: 500, padding: '6px 8px', borderBottom: '1px solid var(--border)', textTransform: 'uppercase', letterSpacing: '0.05em' };
const td = { padding: '6px 8px', borderBottom: '0.5px solid var(--border)', fontSize: 12.5, verticalAlign: 'top' };
const t = (s) => (s ? format(parseISO(s), 'h:mmaaa').replace(':00', '') : '—');

export default function ClientVerificationTab({ client }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const from = new Date(Date.now() - 30 * 86400000).toISOString();
    const { data, error: e } = await supabase.from('visit_review')
      .select('id, evv_visit_id, scheduled_start, scheduled_end, actual_start, actual_end, status, evv_status, billable_units, verified_at, verified_by_name, exceptions, service_name, staff_first, staff_last, staff_full, evv_response_status, evv_response_code, evv_response_message')
      .eq('client_id', client.id).gte('scheduled_start', from)
      .order('scheduled_start', { ascending: false }).limit(120);
    if (e) setError(e.message);
    setRows(data || []); setLoading(false);
  }, [client.id]);
  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => ({
    open: rows.filter(r => !r.verified_at && ['completed', 'missed'].includes(r.status)).length,
    // Only visits that have actually happened; a future visit's missing
    // diagnosis is a scheduling matter, not a verification backlog.
    exceptions: rows.filter(r => !r.verified_at && ['completed', 'missed'].includes(r.status) && (r.exceptions || []).length > 0).length,
    rejected: rows.filter(r => (r.exceptions || []).some(c => c === 'EVV_REJECTED' || c === 'EVV_ERROR')).length,
    verified: rows.filter(r => !!r.verified_at).length,
    units: rows.filter(r => !!r.verified_at).reduce((s, r) => s + Number(r.billable_units || 0), 0),
  }), [rows]);

  if (loading) return <p style={{ color: 'var(--text3)' }}>Loading…</p>;

  return (
    <div>
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>Visit verification · last 30 days</h3>
          <Link to="/hh/verification" className="btn btn-outline btn-sm">Open the desk</Link>
        </div>
        {error && <div style={{ background: 'var(--danger-bg)', borderRadius: 7, padding: '8px 12px', fontSize: 12, color: 'var(--danger)', marginTop: 10 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 18, marginTop: 10, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--text2)' }}>
          <span><b>{stats.open}</b> awaiting verification</span>
          <span style={{ color: stats.exceptions ? 'var(--warn)' : undefined }}><b>{stats.exceptions}</b> with exceptions</span>
          {stats.rejected > 0 && <span style={{ color: 'var(--danger)' }}><b>{stats.rejected}</b> rejected by the aggregator</span>}
          <span><b>{stats.verified}</b> verified</span>
          <span><b>{stats.units}</b> verified units</span>
        </div>
      </div>

      <div style={card}>
        {rows.length === 0 ? (
          <p style={{ fontSize: 12.5, color: 'var(--text3)', margin: 0 }}>
            No CAERA visits for this client in the last 30 days. Visits on a service line still sourced from AxisCare are verified there.
          </p>
        ) : (
          <div style={{ overflowX: 'auto', maxHeight: 460, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['Visit', 'Caregiver', 'Clocked', 'Units', 'State', 'Exceptions'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} style={(r.exceptions || []).some(c => c === 'EVV_REJECTED' || c === 'EVV_ERROR') ? { background: 'var(--danger-bg)' } : undefined}>
                    <td style={td}>{format(parseISO(r.scheduled_start), 'EEE d MMM')}<div style={{ fontSize: 10.5, color: 'var(--text3)' }}>{t(r.scheduled_start)}–{t(r.scheduled_end)} · {r.service_name}</div></td>
                    <td style={td}>{r.staff_last ? `${r.staff_last}, ${r.staff_first || ''}`.replace(/, $/, '') : (r.staff_full || 'Open')}</td>
                    <td style={td}>{r.actual_start ? `${t(r.actual_start)}${r.actual_end ? `–${t(r.actual_end)}` : ''}` : '—'}</td>
                    <td style={td}>{r.billable_units ?? '—'}</td>
                    <td style={td}>
                      {r.verified_at
                        ? <span style={{ color: 'var(--success)' }}>Verified<div style={{ fontSize: 10.5, color: 'var(--text3)' }}>{r.verified_by_name || ''}</div></span>
                        : <span style={{ color: 'var(--text3)' }}>{r.status.replace('_', ' ')}</span>}
                    </td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {(r.exceptions || []).length === 0 && <span className="badge badge-done">Clean</span>}
                        {(r.exceptions || []).map(c => {
                          const meta = EXCEPTION_LABELS[c] || { label: c, severity: 'info' };
                          const st = SEVERITY_STYLE[meta.severity];
                          return <span key={c} className="badge" style={{ color: st.color, background: st.bg, border: `0.5px solid ${st.border}` }}>{meta.label}</span>;
                        })}
                      </div>
                      {r.evv_response_message && ['rejected', 'error'].includes(r.evv_response_status) && (
                        <div style={{ fontSize: 10.5, color: 'var(--danger)', marginTop: 3 }}>{r.evv_response_code ? `${r.evv_response_code} — ` : ''}{r.evv_response_message}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
